import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  getOtConfig, getOt, getProjects, createOt, updateOt, accionOt, uploadOtAdjunto, deleteOtAdjunto, getGruposPlanta,
} from '../api/client'
import PageHeader from '../components/PageHeader'
import FormServicio from '../components/ot/FormServicio'
import FormLote from '../components/ot/FormLote'
import SelectorProyecto from '../components/SelectorProyecto'
import { faltantes } from '../components/ot/otLogica'
import { hoyLocal } from '../components/partes/fechas'

const VACIO = {
  origen: 'proyecto', tipo: 'fabricacion', proyecto_id: '', articulo_codigo: '', articulo_descripcion: '',
  tiene_receta_sap: null, cantidad: '', fecha_requerida: '', descripcion: '', grupo_propuesto_id: '',
}
const TIPOS_DOC = ['bom', 'plano', 'otro']

// Operaciones > Planta > Nueva solicitud: where every OT is born (no OT in
// REM-PMO, no OF in SAP). Saved as a draft, then sent -- a project request
// goes to admission, a restocking proposal to approval. The origin "Servicio"
// (function crear_ot_servicio) creates an internal OT-<n>S at once instead
// (FormServicio). With ?ot=<id> it edits one's own draft (or a returned
// request); ?origen=servicio opens on the service origin.
export default function PlantaOTNueva() {
  const { t } = useTranslation(['operations', 'common'])
  const navigate = useNavigate()
  const [sp] = useSearchParams()
  const editarId = sp.get('ot') ? Number(sp.get('ot')) : null
  const [config, setConfig] = useState(null)
  const [proyectos, setProyectos] = useState([])
  const [grupos, setGrupos] = useState([])
  const [f, setF] = useState(() => ({ ...VACIO, origen: sp.get('origen') || VACIO.origen }))
  const [existentes, setExistentes] = useState([])    // attachments already on the server
  const [nuevos, setNuevos] = useState([])            // [{ tipo, file }] to upload on save
  const [estado, setEstado] = useState(null)          // the edited OT's state
  const [faltan, setFaltan] = useState([])
  const [error, setError] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [cargado, setCargado] = useState(!editarId)
  // One item, or many at once (a batch, L-<n>): a new request only.
  const [modo, setModo] = useState(sp.get('modo') === 'lote' ? 'lote' : 'uno')

  useEffect(() => {
    getOtConfig().then(setConfig).catch(() => setError(t('ot.cargaFallo')))
    getProjects().then(ps => setProyectos(ps.filter(p => p.is_active !== false))).catch(() => {})
    getGruposPlanta().then(setGrupos).catch(() => {})
  }, [t])
  useEffect(() => {
    if (!editarId) return
    getOt(editarId).then(o => {
      if (!o.puede_editar) { setError(t('ot.nueva.noEditable')); return }
      setF({
        origen: o.origen, tipo: o.tipo, proyecto_id: o.proyecto?.id ?? '', articulo_codigo: o.articulo_codigo || '',
        articulo_descripcion: o.articulo_descripcion || '', tiene_receta_sap: o.tiene_receta_sap,
        cantidad: o.cantidad ?? '', fecha_requerida: o.fecha_requerida || '', descripcion: o.descripcion || '',
        grupo_propuesto_id: o.grupo_propuesto?.id ?? '',
      })
      setExistentes(o.adjuntos)
      setEstado(o.estado)
      setCargado(true)
    }).catch(() => setError(t('ot.panel.noEncontrada')))
  }, [editarId, t])

  const funciones = config?.funciones || []
  const origenes = [
    funciones.includes('solicitar') && 'proyecto',
    funciones.includes('proponer_reposicion') && 'reposicion',
    // A draft being edited stays a fabrication request.
    !editarId && funciones.includes('crear_ot_servicio') && 'servicio',
  ].filter(Boolean)
  // A user with only one of the two origins gets that one.
  const origen = origenes.includes(f.origen) ? f.origen : (origenes[0] || f.origen)
  const set = (k, v) => { setF(x => ({ ...x, [k]: v })); setFaltan(fs => fs.filter(n => n !== k)) }
  const adjuntos = [...existentes, ...nuevos]
  const esFalta = (k) => faltan.includes(k)

  const guardar = async (enviar) => {
    const datos = { ...f, origen }
    if (!enviar) {
      const fs = [!String(datos.articulo_codigo || '').trim() && 'articulo_codigo',
        origen === 'proyecto' && !datos.proyecto_id && 'proyecto'].filter(Boolean)
      if (fs.length) { setFaltan(fs); setError(t('ot.faltanDatos', { lista: fs.map(x => t(`ot.faltan.${x}`)).join(', ') })); return }
    }
    if (enviar) {
      const fs = faltantes(datos, { adjuntos })
      if (fs.length) { setFaltan(fs); setError(t('ot.faltanDatos', { lista: fs.map(x => t(`ot.faltan.${x}`)).join(', ') })); return }
    }
    setGuardando(true)
    setError('')
    const cuerpo = {
      origen, proyecto_id: origen === 'proyecto' && datos.proyecto_id ? Number(datos.proyecto_id) : null,
      articulo_codigo: datos.articulo_codigo, articulo_descripcion: datos.articulo_descripcion,
      tiene_receta_sap: datos.tiene_receta_sap, cantidad: datos.cantidad === '' ? null : Number(datos.cantidad),
      fecha_requerida: datos.fecha_requerida || null, descripcion: datos.descripcion,
      grupo_propuesto_id: datos.grupo_propuesto_id ? Number(datos.grupo_propuesto_id) : null,
    }
    let id = editarId
    try {
      const ot = id ? await updateOt(id, cuerpo) : await createOt({ ...cuerpo, tipo: datos.tipo })
      id = ot.id
      const fallidos = []
      for (const a of nuevos) {
        try { await uploadOtAdjunto(id, a.tipo, a.file) } catch { fallidos.push(a.file.name) }
      }
      setNuevos([])
      if (fallidos.length) throw new Error(t('ot.nueva.adjuntoFallo', { lista: fallidos.join(', ') }))
      if (enviar) await accionOt(id, origen === 'reposicion' ? 'proponer' : 'enviar')
      navigate(`/operations/planta/ot?ot=${id}`)
    } catch (err) {
      const d = err.response?.data?.detail
      if (d?.code === 'validacion') {
        setFaltan(d.faltan)
        setError(t('ot.faltanDatos', { lista: d.faltan.map(x => t(`ot.faltan.${x}`, { defaultValue: x })).join(', ') }))
      } else {
        setError(d?.message || (typeof d === 'string' ? d : err.message) || t('ot.panel.fallo'))
      }
      // Saved as a draft already: keep editing that one.
      if (id && !editarId) navigate(`/operations/planta/ot/nueva?ot=${id}`, { replace: true })
    }
    setGuardando(false)
  }

  const quitarExistente = async (a) => {
    try { await deleteOtAdjunto(editarId, a.id); setExistentes(xs => xs.filter(x => x.id !== a.id)) }
    catch { setError(t('ot.panel.fallo')) }
  }

  if (config && !origenes.length) {
    return <div className="p-8 text-sm text-gray-500">{t('ot.nueva.sinFuncion')}</div>
  }
  if (!cargado) return <div className="p-8 text-sm text-gray-400">{error || t('common:loading', { defaultValue: 'Cargando…' })}</div>

  const sinReceta = f.tiene_receta_sap === false
  const lbl = 'block text-xs font-medium text-gray-700 mb-1'
  const inp = (k) => `input ${esFalta(k) ? '!border-rose-400' : ''}`

  return (
    <div className="p-8 max-w-3xl">
      <div className="mb-6">
        <PageHeader title={editarId ? t('ot.nueva.tituloEditar') : t('ot.nueva.titulo')}
          subtitle={estado === 'devuelta' ? t('ot.nueva.subtituloDevuelta') : origen === 'servicio' ? t('ot.servicio.subtitulo') : t('ot.nueva.subtitulo')} />
      </div>

      <div className="space-y-5">
        <Seccion titulo={t('ot.nueva.origen')}>
          <div className={`grid gap-2 ${origenes.length > 2 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
            {origenes.map(o => (
              <button key={o} type="button" onClick={() => set('origen', o)} aria-pressed={origen === o}
                disabled={!!editarId && origenes.length < 2}
                className={`text-left rounded-lg border px-3 py-2.5 transition-colors ${
                  origen === o ? 'border-gray-900 bg-gray-50 ring-1 ring-gray-900' : 'border-gray-200 hover:bg-gray-50'}`}>
                <span className="block text-sm font-medium text-gray-900">{t(`ot.origenes.${o}`)}</span>
                <span className="block text-xs text-gray-500 mt-0.5">{t(`ot.nueva.origenHint.${o}`)}</span>
              </button>
            ))}
          </div>
          {origen !== 'servicio' && !editarId && (
            <div className="mt-3 inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
              {['uno', 'lote'].map(m => (
                <button key={m} type="button" onClick={() => setModo(m)} aria-pressed={modo === m}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${modo === m ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                  {t(`ot.lote.modo.${m}`)}
                </button>
              ))}
            </div>
          )}
          {origen === 'proyecto' && modo !== 'lote' && (
            <div className="mt-3">
              <label className={lbl}>{t('ot.campos.proyecto')} *</label>
              <SelectorProyecto className={inp('proyecto')} proyectos={proyectos} value={f.proyecto_id} onChange={v => set('proyecto_id', v)}
                placeholder={t('ot.nueva.buscarProyecto')} textoVacio={t('ot.nueva.elegir')} />
            </div>
          )}
        </Seccion>

        {origen === 'servicio' ? <FormServicio />
          : modo === 'lote' && !editarId ? <FormLote origen={origen} proyectos={proyectos} grupos={grupos} /> : <>
        <Seccion titulo={t('ot.nueva.queSeNecesita')}>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className={lbl}>{t('ot.campos.articulo_codigo')} *</label>
              <input className={`${inp('articulo_codigo')} font-mono`} value={f.articulo_codigo} placeholder="SE-0104"
                onChange={e => set('articulo_codigo', e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className={lbl}>{t('ot.campos.articulo_descripcion')}</label>
              <input className={inp('articulo_descripcion')} value={f.articulo_descripcion}
                onChange={e => set('articulo_descripcion', e.target.value)} />
            </div>
          </div>
          <p className="text-[11px] text-gray-400 mt-1">{t('ot.nueva.articuloHint')}</p>

          <div className="mt-3">
            <label className={lbl}>{t('ot.campos.tiene_receta_sap')} *</label>
            <div className={`inline-flex rounded-lg border p-0.5 bg-white ${esFalta('tiene_receta_sap') ? 'border-rose-400' : 'border-gray-200'}`}>
              {[true, false].map(v => (
                <button key={String(v)} type="button" onClick={() => set('tiene_receta_sap', v)}
                  className={`px-4 py-1 rounded-md text-sm font-medium ${f.tiene_receta_sap === v ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                  {v ? t('ot.si') : t('ot.no')}
                </button>
              ))}
            </div>
            {sinReceta && (
              <p className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900">{t('ot.nueva.sinRecetaAviso')}</p>
            )}
          </div>

          <div className="grid sm:grid-cols-3 gap-3 mt-3">
            <div>
              <label className={lbl}>{t('ot.campos.cantidad')} *</label>
              <input type="number" min="0" step="any" className={inp('cantidad')} value={f.cantidad}
                onChange={e => set('cantidad', e.target.value)} />
            </div>
            <div>
              <label className={lbl}>{t('ot.campos.fecha_requerida')} *</label>
              <input type="date" min={hoyLocal()} className={inp('fecha_requerida')} value={f.fecha_requerida}
                onChange={e => set('fecha_requerida', e.target.value)} />
            </div>
            <div>
              <label className={lbl}>{t('ot.campos.tipo')}</label>
              <select className="input" value={f.tipo} disabled={!!editarId} onChange={e => set('tipo', e.target.value)}>
                {['fabricacion', 'estructura'].map(x => <option key={x} value={x}>{t(`ot.tipos.${x}`)}</option>)}
              </select>
            </div>
          </div>

          <div className="mt-3">
            <label className={lbl}>{t('ot.campos.grupo_propuesto')}</label>
            <select className="input sm:w-1/2" value={f.grupo_propuesto_id} onChange={e => set('grupo_propuesto_id', e.target.value)}>
              <option value="">{t('ot.nueva.sinGrupo')}</option>
              {grupos.map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
            </select>
            <p className="text-[11px] text-gray-400 mt-1">{t('ot.nueva.grupoHint')}</p>
          </div>

          <div className="mt-3">
            <label className={lbl}>{t('ot.campos.descripcion')}</label>
            <textarea rows={3} className="input" value={f.descripcion} placeholder={t('ot.nueva.descripcionPlaceholder')}
              onChange={e => set('descripcion', e.target.value)} />
          </div>
        </Seccion>

        <Seccion titulo={t('ot.nueva.documentos')}>
          <div className="space-y-3">
            {TIPOS_DOC.map(tipo => (
              <Documentos key={tipo} tipo={tipo} t={t} obligatorio={sinReceta && tipo !== 'otro'}
                falta={esFalta(`adjunto_${tipo}`)}
                existentes={existentes.filter(a => a.tipo === tipo)} nuevos={nuevos.filter(a => a.tipo === tipo)}
                onAgregar={files => { setNuevos(xs => [...xs, ...files.map(file => ({ tipo, file }))]); setFaltan(fs => fs.filter(n => n !== `adjunto_${tipo}`)) }}
                onQuitarNuevo={a => setNuevos(xs => xs.filter(x => x !== a))}
                onQuitarExistente={quitarExistente} />
            ))}
          </div>
        </Seccion>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <button type="button" onClick={() => navigate('/operations/planta/ot')}
            className="border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">
            {t('common:actions.cancel')}
          </button>
          <button type="button" disabled={guardando} onClick={() => guardar(false)}
            className="border border-gray-200 text-gray-800 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-40">
            {t('ot.nueva.guardarBorrador')}
          </button>
          <button type="button" disabled={guardando} onClick={() => guardar(true)}
            className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40">
            {guardando ? t('common:actions.saving') : origen === 'reposicion' ? t('ot.nueva.enviarPropuesta') : t('ot.nueva.enviar')}
          </button>
        </div>
        </>}
      </div>
    </div>
  )
}

function Seccion({ titulo, children }) {
  return (
    <section className="bg-white rounded-xl border border-gray-200 p-5">
      <h2 className="text-sm font-semibold text-gray-900 mb-3">{titulo}</h2>
      {children}
    </section>
  )
}

function Documentos({ tipo, t, obligatorio, falta, existentes, nuevos, onAgregar, onQuitarNuevo, onQuitarExistente }) {
  const ref = useRef(null)
  const vacio = !existentes.length && !nuevos.length
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${falta ? 'border-rose-400 bg-rose-50/40' : 'border-gray-200'}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-gray-800">
          {t(`ot.tiposAdjunto.${tipo}`)}{obligatorio && <span className="text-rose-600"> *</span>}
        </span>
        <button type="button" onClick={() => ref.current?.click()}
          className="text-xs font-medium text-gray-700 border border-gray-200 rounded-md px-2.5 py-1 hover:bg-gray-50">
          {t('ot.nueva.agregarArchivo')}
        </button>
        <input ref={ref} type="file" multiple className="hidden"
          onChange={e => { onAgregar([...e.target.files]); e.target.value = '' }} />
      </div>
      {vacio ? (
        <p className="text-xs text-gray-400 mt-1">{obligatorio ? t('ot.nueva.docObligatorio') : t('ot.nueva.sinArchivos')}</p>
      ) : (
        <ul className="mt-1.5 space-y-1">
          {existentes.map(a => (
            <li key={`e${a.id}`} className="flex items-center gap-2 text-sm">
              <span className="truncate flex-1 text-gray-700">{a.filename}</span>
              <button type="button" onClick={() => onQuitarExistente(a)} className="text-gray-300 hover:text-red-500">×</button>
            </li>
          ))}
          {nuevos.map((a, i) => (
            <li key={`n${i}`} className="flex items-center gap-2 text-sm">
              <span className="truncate flex-1 text-gray-700">{a.file.name}</span>
              <span className="text-[11px] text-gray-400">{t('ot.nueva.porSubir')}</span>
              <button type="button" onClick={() => onQuitarNuevo(a)} className="text-gray-300 hover:text-red-500">×</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
